import { doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { parseUsAddressFromSingleLine } from './parseUsAddressLine';
import { SweepsFirestore } from '../services/sweepsFirestore';
import { formatLocalDateYmd, normalizeSweepScheduledDateInput } from './sweepSchedule';

export type ImportResult = {
  imported: number;
  skipped: number;
  errors: { row: number; message: string }[];
  importBatchId: string;
};

export const normKey = (k: unknown) =>
  String(k ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
export const normVal = (v: unknown) => String(v ?? '').trim();

export function pick(row: Record<string, unknown>, keys: string[]): string {
  const entries = Object.entries(row || {});
  for (const k of keys) {
    const nk = normKey(k);
    const hit = entries.find(([kk]) => normKey(kk) === nk);
    if (hit) return normVal(hit[1]);
  }
  return '';
}

export function toNumber(v: unknown): number | undefined {
  const s = normVal(v);
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

export type ExtractedSweepRow = {
  name: string;
  address: string;
  teamNumber?: number;
  knownInfo: string;
  readerCount?: number;
  latitude?: number;
  longitude?: number;
  stopSequence?: number;
  addressLine1?: string;
  city?: string;
  state?: string;
  zip?: string;
  /** YYYY-MM-DD when import provides a visit / scheduled day. */
  sweepScheduledDate?: string;
  /** Human-readable sweep / folder name — matched or created under `sweepFolders`. */
  sweepFolderName?: string;
};

function excelSerialToYmd(serial: number): string | undefined {
  if (!Number.isFinite(serial) || serial < 20000 || serial > 120000) return undefined;
  const d = new Date((serial - 25569) * 86400000);
  if (Number.isNaN(d.getTime())) return undefined;
  return formatLocalDateYmd(d);
}

export function parseSweepScheduledDateFromCell(v: unknown): string | undefined {
  if (v == null || v === '') return undefined;
  if (typeof v === 'number' && Number.isFinite(v)) {
    const n = v;
    if (Math.abs(n - Math.round(n)) < 1e-9 && n > 20000 && n < 120000) {
      const ymd = excelSerialToYmd(Math.round(n));
      if (ymd) return ymd;
    }
    return undefined;
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
    try {
      return formatLocalDateYmd((v as { toDate: () => Date }).toDate());
    } catch {
      /* noop */
    }
  }
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return formatLocalDateYmd(v);
  }
  const s = normVal(v);
  if (!s) return undefined;
  const iso = normalizeSweepScheduledDateInput(s);
  if (iso) return iso;
  const slash = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (slash) {
    const mo = Number(slash[1]);
    const day = Number(slash[2]);
    const yr = Number(slash[3]);
    const pad = (n: number) => String(n).padStart(2, '0');
    return normalizeSweepScheduledDateInput(`${yr}-${pad(mo)}-${pad(day)}`);
  }
  const n = Number(s);
  if (Number.isFinite(n) && n > 20000 && n < 120000) {
    return excelSerialToYmd(Math.round(n));
  }
  return undefined;
}

export function extractSweepFieldsFromRow(row: Record<string, unknown>): ExtractedSweepRow | null {
  const r = row || {};

  const teamRaw = pick(r, [
    'team',
    'teamnumber',
    'team#',
    'teamno',
    'team_no',
    'teamnum',
    'group',
    'squad',
    'unit',
  ]);
  const teamNumber = toNumber(teamRaw);

  const name = pick(r, [
    'retailer',
    'store',
    'storename',
    'locationname',
    'sitename',
    'name',
    'location',
    'title',
    'site',
    'merchant',
    'business',
  ]);

  let street = pick(r, [
    'address',
    'street',
    'streetaddress',
    'addressline1',
    'address1',
    'fulladdress',
    'locationaddress',
  ]);
  let city = pick(r, ['city', 'town', 'municipality']);
  let state = pick(r, ['state', 'province', 'region', 'st']);
  let zip = pick(r, ['zip', 'zipcode', 'postal', 'postalcode', 'zip_code']);

  let address = pick(r, ['fulladdress', 'completeaddress', 'mailingaddress']);
  if (!address) {
    const parts = [street, city, state, zip].filter((p) => !!normVal(p));
    address = parts.join(', ');
  }
  if (!address && street) {
    address = [street, city, state, zip].filter((p) => !!normVal(p)).join(', ');
  }

  const needsStructured = !normVal(city) || !normVal(state) || !normVal(zip);
  const parseCandidate = normVal(street) || normVal(address);
  if (needsStructured && parseCandidate) {
    const p = parseUsAddressFromSingleLine(parseCandidate);
    if (p.state && p.zip) {
      if (!normVal(city)) city = p.city;
      if (!normVal(state)) state = p.state;
      if (!normVal(zip)) zip = p.zip;
      if (p.addressLine1) {
        const streetMatches = normVal(street) === parseCandidate;
        const addressMatches = normVal(address) === parseCandidate && !normVal(street);
        if (streetMatches || addressMatches) {
          street = p.addressLine1;
        }
      }
      const rebuilt = [street || p.addressLine1, city, state, zip].filter((x) => !!normVal(x)).join(', ');
      if (rebuilt) address = rebuilt;
    }
  }

  const knownInfo = pick(r, ['knowninfo', 'known_information', 'notes', 'info', 'description', 'comments']);
  const readerCountRaw = pick(r, ['readercount', 'readers', 'reader_count', 'pumps']);
  const latitudeRaw = pick(r, ['latitude', 'lat']);
  const longitudeRaw = pick(r, ['longitude', 'lng', 'lon', 'long']);
  const stopSeqRaw = pick(r, [
    'stop',
    'stop#',
    'stopnumber',
    'stop_no',
    'stopnum',
    'sequence',
    'seq',
    'order',
    'routeorder',
    'stoporder',
    'routesequence',
  ]);

  const schedKeys = [
    'sweepscheduleddate',
    'sweepvisitdate',
    'sweepdate',
    'scheduledsweep',
    'scheduleddate',
    'visitdate',
    'scheduledvisit',
    'duedate',
    'routevisitdate',
  ];
  const schedPick = pick(r, schedKeys);
  const schedParsed = schedPick ? parseSweepScheduledDateFromCell(schedPick) : undefined;

  const sweepFolderName = pick(r, [
    'sweepfolder',
    'sweepfoldername',
    'sweepname',
    'folder',
    'foldername',
    'routename',
    'route',
    'batch',
    'batchname',
    'sweepgroup',
    'sweep_group',
  ]);

  if (!name || !address) {
    return null;
  }

  const readerCount = toNumber(readerCountRaw);
  const latitude = toNumber(latitudeRaw);
  const longitude = toNumber(longitudeRaw);
  const stopSequence = toNumber(stopSeqRaw);

  const addressLine1 = street || pick(r, ['addressline1', 'addr1']) || '';

  return {
    name,
    address,
    ...(typeof teamNumber === 'number' ? { teamNumber: Math.round(teamNumber) } : {}),
    knownInfo: knownInfo || '',
    ...(typeof readerCount === 'number' ? { readerCount: Math.round(Math.max(0, readerCount)) } : {}),
    ...(typeof latitude === 'number' ? { latitude } : {}),
    ...(typeof longitude === 'number' ? { longitude } : {}),
    ...(typeof stopSequence === 'number' ? { stopSequence: Math.round(stopSequence) } : {}),
    ...(addressLine1 ? { addressLine1 } : {}),
    ...(city ? { city } : {}),
    ...(state ? { state } : {}),
    ...(zip ? { zip } : {}),
    ...(schedParsed ? { sweepScheduledDate: schedParsed } : {}),
    ...(normVal(sweepFolderName) ? { sweepFolderName: normVal(sweepFolderName) } : {}),
  };
}

export async function commitSweepRowsToFirestore(opts: {
  masterOwnerID: string;
  ownerID?: string;
  importBatchId: string;
  rows: ExtractedSweepRow[];
  /** When set, every imported row is assigned this `sweepFolders` doc id (import dialog flow). */
  forcedSweepFolderId?: string;
  /** When set, every row gets this visit / browse day (YYYY-MM-DD). */
  forcedSweepScheduledDate?: string;
}): Promise<{ imported: number }> {
  const masterOwnerID = normVal(opts.masterOwnerID);
  if (!masterOwnerID) throw new Error('masterOwnerID is required');

  const forcedFolder = normVal(opts.forcedSweepFolderId || '');
  const forcedDate = normVal(opts.forcedSweepScheduledDate || '');

  let imported = 0;

  const BATCH_SIZE = 400;
  let batch = writeBatch(db);
  let ops = 0;

  const commitBatch = async () => {
    if (ops === 0) return;
    await batch.commit();
    batch = writeBatch(db);
    ops = 0;
  };

  const seenFolder = new Set<string>();
  const folderNamesInOrder: string[] = [];
  if (!forcedFolder) {
    for (const ex of opts.rows) {
      const fn = normVal(ex.sweepFolderName || '');
      if (!fn) continue;
      const low = fn.toLowerCase();
      if (seenFolder.has(low)) continue;
      seenFolder.add(low);
      folderNamesInOrder.push(fn);
    }
  }
  const folderIdByLowerName =
    folderNamesInOrder.length > 0
      ? await SweepsFirestore.ensureSweepFolderIdsForNames(masterOwnerID, opts.ownerID, folderNamesInOrder)
      : new Map<string, string>();

  for (const ex of opts.rows) {
    if (!ex.name || !ex.address) continue;

    const docId = SweepsFirestore.makeSweepLocationId({
      masterOwnerID,
      name: ex.name,
      address: ex.address,
      teamNumber: ex.teamNumber,
    });
    const ref = doc(db, 'sweepLocations', docId);

    const payload: Record<string, unknown> = {
      masterOwnerID,
      name: ex.name,
      address: ex.address,
      knownInfo: ex.knownInfo || '',
      importBatchId: opts.importBatchId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (typeof ex.teamNumber === 'number') payload.teamNumber = ex.teamNumber;
    if (typeof ex.readerCount === 'number') payload.readerCount = ex.readerCount;
    if (typeof ex.latitude === 'number') payload.latitude = ex.latitude;
    if (typeof ex.longitude === 'number') payload.longitude = ex.longitude;
    if (typeof ex.stopSequence === 'number') payload.stopSequence = ex.stopSequence;
    if (ex.addressLine1) payload.addressLine1 = ex.addressLine1;
    if (ex.city) payload.city = ex.city;
    if (ex.state) payload.state = ex.state;
    if (ex.zip) payload.zip = ex.zip;
    if (opts.ownerID) payload.createdByOwnerID = normVal(opts.ownerID);
    if (forcedDate && /^\d{4}-\d{2}-\d{2}$/.test(forcedDate)) {
      payload.sweepScheduledDate = forcedDate;
    } else if (ex.sweepScheduledDate) {
      payload.sweepScheduledDate = ex.sweepScheduledDate;
    }
    if (forcedFolder) {
      payload.sweepFolderId = forcedFolder;
    } else {
      const fName = normVal(ex.sweepFolderName || '');
      if (fName) {
        const fid = folderIdByLowerName.get(fName.toLowerCase());
        if (fid) payload.sweepFolderId = fid;
      }
    }

    batch.set(ref, payload, { merge: true });
    ops++;
    imported++;

    if (ops >= BATCH_SIZE) await commitBatch();
  }

  await commitBatch();

  return { imported };
}
